import { Badge, Box, Divider, Flex, Text } from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import { GynMedicationsAndSupplementsData } from '@hb/shared'
import { useMemo } from 'react'
import { SectionLabel } from '../../SectionLabel'

const formatAdministeredVia: Record<string, string> = {
  oral: 'Oral',
  sublingual: 'Sublingual',
  im: 'IM',
  iv: 'IV',
  topical: 'Topical',
  rectal: 'Rectal',
  vaginal: 'Vaginal',
  subcutaneous: 'Subcutaneous',
  inhalation: 'Inhalation',
  ophthalmic: 'Ophthalmic',
  intrauterine: 'Intrauterine',
  buccal: 'Buccal',
}

const MedicationRows = ({ data }: { data: GynMedicationsAndSupplementsData | undefined }) => {
  const activeMeds = useMemo(() => (data?.medications ?? []).filter(m => m.currentlyTaking), [data])
  const activeSupplements = useMemo(
    () => (data?.supplements ?? []).filter(s => s.currentlyTaking),
    [data],
  )

  if (!activeMeds.length && !activeSupplements.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No medications or supplements on file.
      </Text>
    )
  }

  return (
    <Box>
      {activeMeds.length > 0 && (
        <Box mb={activeSupplements.length ? 2 : 0}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>
            Medications
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {activeMeds.map((med, i) => (
              <Badge
                key={i}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                colorScheme="blue"
                variant="subtle"
                fontWeight={600}>
                {med.name}
                {med.dosage ? ` ${med.dosage}` : ''}
                {med.frequency ? ` — ${med.frequency}` : ''}
                {med.administeredVia
                  ? ` (${formatAdministeredVia[med.administeredVia] ?? med.administeredVia})`
                  : ''}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
      {activeMeds.length > 0 && activeSupplements.length > 0 && <Divider my={2} />}
      {activeSupplements.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>
            Supplements
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {activeSupplements.map((sup, i) => (
              <Badge
                key={i}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                colorScheme="teal"
                variant="subtle"
                fontWeight={600}>
                {sup.name}
                {sup.dosage ? ` ${sup.dosage}` : ''}
                {sup.frequency ? ` — ${sup.frequency}` : ''}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

export const ExamMedicationsView = () => {
  const { user } = useProfile()
  const medsData = user?.gyn?.mergedData?.medicationsAndSupplements

  return (
    <Box w="100%">
      <SectionLabel>Medications &amp; Supplements</SectionLabel>
      <MedicationRows data={medsData} />
    </Box>
  )
}
